export enum ListConnectType {
	EMAIL = "EMAIL",
	LINKEDIN = "LINKEDIN",
	BOTH = "BOTH",
}

export enum ListStatus {
	PAUSED = "PAUSED",
	ACTIVE = "ACTIVE",
	ERROR = "ERROR",
	STOPPING = "STOPPING",
}

export enum ListRunningStatus {
	PAUSED = "PAUSED",
	ACTIVE = "ACTIVE",
	ALL = "ALL",
}

/**
 * Content filter for the Lead List Overview "Show" dropdown. Each value narrows the visible lists
 * by what they contain; it is independent of the archived toggle ({@link ArchivedView}).
 */
export enum ListContentFilter {
	ALL = "ALL",
	WITH_LEADS = "WITH_LEADS",
	WITH_CAMPAIGNS = "WITH_CAMPAIGNS",
	WITH_ACTIVE_CAMPAIGNS = "WITH_ACTIVE_CAMPAIGNS",
}

/** Archived visibility toggle, orthogonal to {@link ListContentFilter}. */
export enum ArchivedView {
	HIDE = "HIDE",
	SHOW = "SHOW",
}

export enum ListOwnership {
	MINE = "MINE",
	TEAM = "TEAM",
	ALL = "ALL",
}

/**
 * How the lean-list endpoint treats the `assignStatus` relevance set: `FILTER` hides irrelevant
 * lists (the default), `RANK` keeps them but sorts them last so the messaging scope filters can
 * explain why a list has nothing here instead of making it vanish (PREC-3834).
 */
export enum ListRelevanceMode {
	FILTER = "FILTER",
	RANK = "RANK",
}

export enum ArchiveStatus {
	ARCHIVED = "ARCHIVED",
	NOT_ARCHIVED = "NOT_ARCHIVED",
}

export enum ListItemType {
	SOURCE = "SOURCE",
	FILTER = "FILTER",
}

export enum SourceType {
	APOLLO = "APOLLO",
	LINKEDIN_EVENTS = "LINKEDIN_EVENTS",
	NORTHDATA = "NORTHDATA",
	CUSTOM_EVENTS = "CUSTOM_EVENTS",
}

export enum SearchBase {
	PERSON = "PERSON",
	COMPANY = "COMPANY",
	SOURCE = "SOURCE",
}

export enum UpdateType {
	SOURCE = "SOURCE",
	SCORE = "SCORE",
	ACCOUNT_FILTER = "ACCOUNT_FILTER",
	LEAD_FILTER = "LEAD_FILTER",
	ENRICHMENTS = "ENRICHMENTS",
	CAMPAIGNS = "CAMPAIGNS",
	SETTINGS = "SETTINGS",
	STATUS = "STATUS",
	CUSTOM_COLUMN = "CUSTOM_COLUMN",
	STATUS_WITH_REFETCH = "STATUS_WITH_REFETCH",
	CAMPAIGN_CONFIG = "CAMPAIGN_CONFIG",
	/** Add/overwrite only the campaigns in the payload; siblings stay untouched (PREC-4026). */
	CAMPAIGN_CONFIG_MERGE = "CAMPAIGN_CONFIG_MERGE",
	COPYWRITING = "COPYWRITING",
}

/**
 * Where a lead sits in a list.
 *
 * `SKIPPED` (PREC-4094) marks a lead the list refused because it is already on another list of the
 * company. **It is never rendered.** There is no Show-dropdown bucket for it, no count, and no
 * `InclusionFilterStatus.SKIPPED` to request it with — a lead that was never added is not something
 * the customer has to look at. It exists as a row so support can explain a short list.
 */
export enum InclusionStatus {
	ADDED = "ADDED",
	NOT_FOUND = "NOT_FOUND",
	UNSURE = "UNSURE",
	BLACKLIST = "BLACKLIST",
	SKIPPED = "SKIPPED",
}

export enum InclusionFilterStatus {
	ADDED = "ADDED",
	UNSURE = "UNSURE",
	NOT_FOUND = "NOT_FOUND",
	BOTH = "BOTH",
	BLACKLIST = "BLACKLIST",
}

export enum PriorityFilterStatus {
	ALL = "ALL",
	ALL_WITH_PRIORITY = "ALL_WITH_PRIORITY",
	P1 = "P1",
	P2 = "P2",
	P3 = "P3",
	DQ = "DQ",
	/** "?" — a lead scoring could not judge (stored `overallScore` "Unsure", PREC-4075). */
	UNSURE = "UNSURE",
}

export enum SignalFilterStatus {
	ALL = "ALL",
	ALL_WITH_SIGNAL = "ALL_WITH_SIGNAL",
	S1 = "S1",
	S2 = "S2",
	S3 = "S3",
	DQ = "DQ",
}

export enum MessagesAssignStatus {
	UNSCHEDULED = "UNSCHEDULED",
	SCHEDULED = "SCHEDULED",
	RUNNING = "RUNNING",
	SAVED_FOR_LATER = "SAVED_FOR_LATER",
	REPLIES = "REPLIES",
	ENDED = "ENDED",
	PAUSED = "PAUSED",
}

export enum FollowUpType {
	EXISTING_CONNECTION = "EXISTING_CONNECTION",
	EXISTING_THREAD = "EXISTING_THREAD",
}

export enum AssignStatus {
	DRAFT = "DRAFT",
	SAVED_FOR_LATER = "SAVED_FOR_LATER",
	ASSIGNING = "ASSIGNING",
	ASSIGNED = "ASSIGNED",
	RUNNING = "RUNNING",
	BLACKLIST = "BLACKLIST",
	NOT_NOW = "NOT_NOW",
	NO_REPLY = "NO_REPLY",
	REPLIES = "REPLIES",
	TO_BE_NURTURED = "TO_BE_NURTURED",
	DO_NOT_CONTACT = "DO_NOT_CONTACT",
	ENDED = "ENDED",
	PAUSED = "PAUSED",
	MESSAGE_SENT = "MESSAGE_SENT",
	ARCHIVED = "ARCHIVED",
	FOLLOW_UP = "FOLLOW_UP",
	DONE = "DONE",
	UNDER_REVIEW = "UNDER_REVIEW",
	TRACKED_PROFILE = "TRACKED_PROFILE",
}

export enum AssignStatusFilter {
	ALL = "ALL",
	DRAFT = "DRAFT",
	SAVED_FOR_LATER = "SAVED_FOR_LATER",
	ASSIGNING = "ASSIGNING",
	ASSIGNED = "ASSIGNED",
	RUNNING = "RUNNING",
	BLACKLIST = "BLACKLIST",
	NOT_NOW = "NOT_NOW",
	NO_REPLY = "NO_REPLY",
	REPLIES = "REPLIES",
	TO_BE_NURTURED = "TO_BE_NURTURED",
	DO_NOT_CONTACT = "DO_NOT_CONTACT",
	ENDED = "ENDED",
	PAUSED = "PAUSED",
	MESSAGE_SENT = "MESSAGE_SENT",
	ARCHIVED = "ARCHIVED",
	FOLLOW_UP = "FOLLOW_UP",
	DONE = "DONE",
	UNDER_REVIEW = "UNDER_REVIEW",
	TRACKED_PROFILE = "TRACKED_PROFILE",
}

export enum AssignType {
	CAMPAIGN = "CAMPAIGN",
	PERSON = "PERSON",
}

/**
 * Outbox list-level tab. Mirrors the API OutboxView enum: the backend splits the
 * prospect set into upcoming sends (SCHEDULED) vs. already-sent leads (SENT).
 */
export enum OutboxView {
	SENT = "SENT",
	SCHEDULED = "SCHEDULED",
}

/**
 * Outbox status-tab buckets (PREC-3850). Mirrors the API `OutboxTab` enum — the
 * backend classifies each lead into exactly one bucket; the "All" tab sends its
 * configured subset of these as the `outboxTabs` request param.
 */
export enum OutboxTab {
	SCHEDULED = "SCHEDULED",
	IN_PROGRESS = "IN_PROGRESS",
	ENDED = "ENDED",
	PAUSED = "PAUSED",
	ERROR = "ERROR",
}

export enum ListSourceType {
	LI_EVENTS = "LI_EVENTS",
	LI_PEOPLE = "LI_PEOPLE",
	LI_COMPANY_ACTIVITY = "LI_COMPANY_ACTIVITY",
	LI_PROFILE_VISITS = "LI_PROFILE_VISITS",
	LI_PROFILE = "LI_PROFILE",
	CUSTOM_EVENTS = "CUSTOM_EVENTS",
	CSV_ENRICHMENTS = "CSV_ENRICHMENTS",
	JOB_CHANGE = "JOB_CHANGE",
	JOBS = "JOBS",
	MAPS = "MAPS",
	LI_POST = "LI_POST",
	LI_SN = "LI_SN",
	PARALLEL_AI = "PARALLEL_AI",
	FUNDING_ROUNDS = "FUNDING_ROUNDS",
	EMPTY = "EMPTY",
	LEAD_DATABASE = "LEAD_DATABASE",
	COMPANY_ENRICH = "COMPANY_ENRICH",
	TRACKER = "TRACKER",
	SMART_LIST = "SMART_LIST",
	LI_CONNECTIONS = "LI_CONNECTIONS",
}

export enum ListType {
	ONCE = "ONCE",
	ON_GOING = "ON_GOING",
}

/** Per-list/-prospect visibility for LinkedIn connection enrichment (PREC-3531). */
export enum ListVisibility {
	PRIVATE = "PRIVATE",
	PUBLIC = "PUBLIC",
}

/** How much of the user's network the connection-enrichment list ingests. */
export enum EnrichLinkedinConnectionsMode {
	ALL = "ALL",
	NEW_ONLY = "NEW_ONLY",
}

/** How often an ON_GOING list re-runs. Mirrors the backend `ListRecurrence`. */
export enum ListRecurrence {
	DAILY = "DAILY",
	WEEKLY = "WEEKLY",
	MONTHLY = "MONTHLY",
}

export enum SmartListFrequency {
	DAILY = "DAILY",
	WEEKLY = "WEEKLY",
	MONTHLY = "MONTHLY",
}

export enum SingleOrMultiSource {
	SINGLE = "SINGLE",
	MULTI = "MULTI",
}

export const ListSourcesWithAdditionalSources = [
	ListSourceType.CSV_ENRICHMENTS,
	ListSourceType.CUSTOM_EVENTS,
	ListSourceType.LI_EVENTS,
	ListSourceType.LI_PEOPLE,
	ListSourceType.LI_COMPANY_ACTIVITY,
	ListSourceType.JOBS,
	ListSourceType.LI_PROFILE_VISITS,
	ListSourceType.LI_PROFILE,
	ListSourceType.MAPS,
	ListSourceType.LI_POST,
	ListSourceType.JOB_CHANGE,
	ListSourceType.PARALLEL_AI,
];

export enum ListAction {
	GENERAL = "GENERAL",
	ADDED = "ADDED",
	UNSURE = "UNSURE",
}

export enum CustomColumnTypes {
	AI_SNIPPET = "AI_SNIPPET",
	TEXT = "TEXT",
	NUMBER = "NUMBER",
	BOOLEAN = "BOOLEAN",
	LINK = "LINK",
	AI_PROMPT = "AI_PROMPT",
	GOOGLE_NEWS = "GOOGLE_NEWS",
	TECHNOLOGY = "TECHNOLOGY",
	DEPARTMENT_SIZE = "DEPARTMENT_SIZE",
	LINKEDIN_POST = "LINKEDIN_POST",
	RANDOM_VALUE = "RANDOM_VALUE",
	JOB_OFFER = "JOB_OFFER",
	JOB_CHANGE = "JOB_CHANGE",
	LEAD_SCORING = "LEAD_SCORING",
	LINKEDIN_POST_ENGAGEMENT = "LINKEDIN_POST_ENGAGEMENT",
	DEEP_RESEARCH = "DEEP_RESEARCH",
}

export enum CustomColumnFrequency {
	ONCE = "ONCE",
	DAILY = "DAILY",
	WEEKLY = "WEEKLY",
	MONTHLY = "MONTHLY",
	QUATERLY = "QUATERLY",
	HALFYEARLY = "HALFYEARLY",
	YEARLY = "YEARLY",
}

export enum AiApplyType {
	NONE = "NONE",
	ALL = "ALL",
	ALL_INCLUDING_ASSIGNED = "ALL_INCLUDING_ASSIGNED",
	FIRST_10 = "FIRST_10",
	CURRENTLY_FILTERED = "CURRENTLY_FILTERED",
	CURRENTLY_SELECTED = "CURRENTLY_SELECTED",
}

export enum ListProcessingStatus {
	IDLE = "IDLE",
	ERROR = "ERROR",
	PROCESSING = "PROCESSING",
	DESKTOP_APP = "DESKTOP_APP",
	API_WAITING = "API_WAITING",
	WAITING = "WAITING",
	DONE = "DONE",
	NO_USER_FOUND = "NO_USER_FOUND",
	NOT_ENOUGH_CREDITS = "NOT_ENOUGH_CREDITS",
	WAITING_FOR_SESSION = "WAITING_FOR_SESSION",
}

export enum ResponseStatus {
	DRAFT = "DRAFT",
	INTERESTED = "INTERESTED",
	NOT_INTERESTED = "NOT_INTERESTED",
	SENT = "SENT",
	OPENED = "OPENED",
	CLICKED = "CLICKED",
	REPLIED = "REPLIED",
	BOUNCED = "BOUNCED",
	UNSUBSCRIBED = "UNSUBSCRIBED",
	LINKEDIN_VISIT = "LINKEDIN_VISIT",
	LINKEDIN_INVITED = "LINKEDIN_INVITED",
	LINKEDIN_INVITE_ACCEPTED = "LINKEDIN_INVITE_ACCEPTED",
	NOT_NOW = "NOT_NOW",
}

export enum ReassignType {
	CAMPAIGN = "CAMPAIGN",
	LIST = "LIST",
	PERSON = "PERSON",
}

export enum EnrichmentType {
	AI_AGENT = "AI_AGENT",
	CHROME_EXTENSION = "CHROME_EXTENSION",
}

export enum SelectType {
	ALL = "ALL",
	CURRENT_PAGE = "CURRENT_PAGE",
}

export enum AddToListMode {
	COPY = "copy",
	MOVE = "move",
}

export enum GetContactInformation {
	NONE = "NONE",
	ALWAYS = "ALWAYS",
	ONLY_WHEN_NO_EMAIL_FOUND = "ONLY_WHEN_NO_EMAIL_FOUND",
}

export enum GetEmailInformation {
	NONE = "NONE",
	TRY_TO_FIND_EMAIL = "TRY_TO_FIND_EMAIL",
	WHEN_ASSIGNED = "WHEN_ASSIGNED",
	WHEN_TRIED_TO_SEND_EMAIL = "WHEN_TRIED_TO_SEND_EMAIL",
}

export enum GetPhoneInformation {
	NONE = "NONE",
	TRY_TO_FIND_PHONE = "TRY_TO_FIND_PHONE",
}

export enum JobChangeStatus {
	PENDING = "PENDING",
	CHECKED = "CHECKED",
	CHANGED = "CHANGED",
	NO_CHANGE = "NO_CHANGE",
}

export enum JobChangeIntervalUnit {
	DAYS = "DAYS",
	WEEKS = "WEEKS",
	MONTHS = "MONTHS",
	YEARS = "YEARS",
}

export enum AsyncProcessingStatus {
	PENDING = "PENDING",
	PROCESSING = "PROCESSING",
	DONE = "DONE",
	FAILED = "FAILED",
}

export enum AsyncProcessingType {
	AI_SNIPPET = "AI_SNIPPET",
	FIND_PERSON = "FIND_PERSON",
}
